import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useWorkspace } from "../../app/workspace";
import { Banner } from "../../components/ui/Banner";
import { Button, ButtonLink } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ErrorState } from "../../components/ui/ErrorState";
import { Field, Input, Select } from "../../components/ui/Field";
import { Loading, PageHeader } from "../../components/ui/Page";
import { useAction } from "../../hooks/useAction";
import { useResource } from "../../hooks/useResource";
import { streamsApi } from "../../lib/api/streams";
import { videosApi } from "../../lib/api/videos";
import {
  allowedOptions, defaultRtmpUrl, emptyForm, formFromStream, toCreateInput, toPatch, validateForm, type StreamFormValues,
} from "../../lib/stream-form";
import { isEditable, PLATFORM_LABEL, PLATFORMS } from "../../lib/stream-status";
import { PlaylistPicker } from "./PlaylistPicker";

/** Create (/streams/new) and edit (/streams/:id/edit) share one form. */
export function StreamFormPage() {
  const { id } = useParams();
  const mode = id ? "edit" : "create";
  const { workspace } = useWorkspace();
  const existing = useResource((signal) => (id ? streamsApi.get(id, signal) : Promise.resolve(null)), [id]);
  const library = useResource((signal) => videosApi.list(workspace.id, signal), [workspace.id]);

  if (existing.error !== undefined) return <ErrorState error={existing.error} onRetry={() => void existing.reload()} />;
  if (library.error !== undefined) return <ErrorState error={library.error} onRetry={() => void library.reload()} />;
  if (existing.loading || !library.data) return <Loading />;
  if (existing.data && !isEditable(existing.data)) {
    return (
      <Banner tone="warn" action={<ButtonLink to={`/streams/${existing.data.id}`} size="sm">back to stream</ButtonLink>}>
        Stop the stream before changing its settings.
      </Banner>
    );
  }
  const initial = existing.data ? formFromStream(existing.data) : emptyForm();
  return <StreamForm mode={mode} streamId={id} initial={initial} videos={library.data.videos} keyLast4={existing.data?.streamKeyLast4} />;
}

interface FormProps {
  mode: "create" | "edit";
  streamId?: string;
  initial: StreamFormValues;
  videos: Parameters<typeof PlaylistPicker>[0]["videos"];
  keyLast4?: string;
}

function StreamForm({ mode, streamId, initial, videos, keyLast4 }: FormProps) {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const [v, setV] = useState(initial);
  const [touched, setTouched] = useState(false);
  const set = <K extends keyof StreamFormValues>(k: K, value: StreamFormValues[K]) => setV((x) => ({ ...x, [k]: value }));

  const opts = useMemo(() => allowedOptions(workspace.plan, v.platform), [workspace.plan, v.platform]);
  // Keep quality and fps inside what the plan and platform allow when either changes.
  useEffect(() => {
    if (!opts.qualities.includes(v.quality)) set("quality", opts.qualities.at(-1)!);
    if (!opts.fps.includes(v.fps)) set("fps", opts.fps.at(-1)!);
  }, [opts, v.quality, v.fps]);

  const errors = validateForm(v, mode, videos);
  const save = useAction(async () => {
    if (mode === "create") {
      const s = await streamsApi.create(workspace.id, toCreateInput(v));
      navigate(`/streams/${s.id}`, { replace: true });
      return;
    }
    const { patch, playlist } = toPatch(initial, v);
    if (Object.keys(patch).length) await streamsApi.update(streamId!, patch);
    if (playlist) await streamsApi.setPlaylist(streamId!, playlist);
    navigate(`/streams/${streamId}`, { replace: true });
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length === 0) void save.run();
  };
  const err = (k: keyof StreamFormValues) => (touched ? errors[k] : undefined);
  const fixedUrl = defaultRtmpUrl(v.platform);

  return (
    <>
      <PageHeader
        caption={<Link to={streamId ? `/streams/${streamId}` : "/streams"}>{streamId ? "stream" : "streams"}</Link>}
        title={mode === "create" ? "New stream" : `Edit ${initial.name}`}
      />
      <Card raised as="div" className="p-6 sm:p-8">
        <form noValidate onSubmit={submit} className="flex flex-col gap-6">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="name" error={err("name")}>
              {({ id, describedBy, invalid }) => (
                <Input id={id} value={v.name} maxLength={200} onChange={(e) => set("name", e.target.value)}
                  aria-describedby={describedBy} aria-invalid={invalid} placeholder="lofi-247" />
              )}
            </Field>
            <Field label="platform" hint={mode === "edit" ? "The platform can't change; create a new stream instead." : undefined}>
              {({ id, describedBy }) => (
                <Select id={id} value={v.platform} disabled={mode === "edit"} aria-describedby={describedBy}
                  onChange={(e) => set("platform", e.target.value as StreamFormValues["platform"])}>
                  {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="rtmp url" error={err("rtmpUrl")} hint={fixedUrl ? "Set for this platform." : "From your streaming server's settings."}>
              {({ id, describedBy, invalid }) => fixedUrl
                ? <Input id={id} value={fixedUrl} readOnly aria-describedby={describedBy} className="text-muted" />
                : <Input id={id} value={v.rtmpUrl} onChange={(e) => set("rtmpUrl", e.target.value)} placeholder="rtmps://example.com/live"
                    inputMode="url" aria-describedby={describedBy} aria-invalid={invalid} />}
            </Field>
            <Field
              label="stream key"
              error={err("streamKey")}
              hint={mode === "edit" ? `Leave empty to keep the current key (••••${keyLast4 ?? ""}).` : "Stored encrypted; only its last 4 characters are shown again."}
            >
              {({ id, describedBy, invalid }) => (
                <Input id={id} type="password" autoComplete="off" value={v.streamKey} onChange={(e) => set("streamKey", e.target.value)}
                  aria-describedby={describedBy} aria-invalid={invalid} />
              )}
            </Field>
            <Field label="quality" hint={opts.qualities.length < 2 ? "4K needs a plan and platform that allow it." : undefined}>
              {({ id, describedBy }) => (
                <Select id={id} value={v.quality} aria-describedby={describedBy} onChange={(e) => set("quality", e.target.value as StreamFormValues["quality"])}>
                  {opts.qualities.map((q) => <option key={q} value={q}>{q === "4k" ? "4K" : "HD (1080p)"}</option>)}
                </Select>
              )}
            </Field>
            <Field label="frame rate">
              {({ id }) => (
                <Select id={id} value={v.fps} onChange={(e) => set("fps", Number(e.target.value) as StreamFormValues["fps"])}>
                  {opts.fps.map((f) => <option key={f} value={f}>{f} fps</option>)}
                </Select>
              )}
            </Field>
          </div>

          <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
            <legend className="mb-1.5">when the playlist ends</legend>
            {(["loop", "once"] as const).map((m) => (
              <label key={m} className="flex min-h-11 items-center gap-2">
                <input type="radio" name="loopMode" value={m} checked={v.loopMode === m} onChange={() => set("loopMode", m)} className="size-4 accent-accent-strong" />
                {m === "loop" ? "start again (loop)" : "stop the stream"}
              </label>
            ))}
          </fieldset>

          <div className="grid gap-5 md:grid-cols-2">
            <Field label="start at (optional)" hint="Empty: starts when you press start.">
              {({ id, describedBy }) => (
                <Input id={id} type="datetime-local" value={v.scheduledStartAt} aria-describedby={describedBy}
                  onChange={(e) => set("scheduledStartAt", e.target.value)} />
              )}
            </Field>
            <Field label="stop at (optional)" error={err("scheduledEndAt")} hint="Empty: runs until you press stop.">
              {({ id, describedBy, invalid }) => (
                <Input id={id} type="datetime-local" value={v.scheduledEndAt} aria-describedby={describedBy} aria-invalid={invalid}
                  onChange={(e) => set("scheduledEndAt", e.target.value)} />
              )}
            </Field>
          </div>

          <PlaylistPicker videos={videos} value={v.videoIds} onChange={(ids) => set("videoIds", ids)} quality={v.quality} fps={v.fps} error={err("videoIds")} />

          {save.error && <Banner tone="bad">{save.error}</Banner>}
          <div className="flex flex-wrap gap-3">
            <Button type="submit" variant="primary" pending={save.pending} pendingLabel="saving…">
              {mode === "create" ? "create stream →" : "save changes"}
            </Button>
            <ButtonLink to={streamId ? `/streams/${streamId}` : "/streams"}>cancel</ButtonLink>
          </div>
        </form>
      </Card>
    </>
  );
}
